import React, { useState, useEffect } from 'react';
import { Modal } from '../../common/Modal';
import { LoadingSpinner } from '../../common/LoadingSpinner';
import { BedList } from './BedList';
import { PilgrimSelector } from './PilgrimSelector';
import { useAccommodationStore } from '../../../stores/accommodationStore';
import { usePackagesStore } from '../../../stores/packagesStore';
import { RoomDetails } from '../../../types/room';
import { useRoomPermissions } from '../../../hooks/useRoomPermissions';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import toast from 'react-hot-toast';
import { Edit, UserPlus } from 'lucide-react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { calculateAge } from '../../../utils/dateUtils';
import { EmployeeGuideModal } from './EmployeeGuideModal';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tableId: string;
  roomNumber: number;
};

export const RoomViewModal = ({ isOpen, onClose, tableId, roomNumber }: Props) => {
  const { translations } = useLanguage();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [room, setRoom] = useState<RoomDetails | null>(null);
  const [selectedBed, setSelectedBed] = useState<number | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isEmployeeGuideModalOpen, setIsEmployeeGuideModalOpen] = useState(false);
  const [newRoomNumber, setNewRoomNumber] = useState<string>('');
  const [commentText, setCommentText] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const [dateRange, setDateRange] = useState<{
    checkIn: string;
    checkOut: string;
  } | null>(null);

  const {
    tables,
    getRoomDetails,
    assignPilgrim,
    removePilgrim,
    updateRoomNumber,
    addComment,
    deleteComment,
  } = useAccommodationStore();
  
  const { packages } = usePackagesStore();
  
  const { canAssignPilgrims, canRemovePilgrims } = useRoomPermissions();
  const userRole = localStorage.getItem('userRole');
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName');

  const table = tables[tableId];

  // Get estimated time for a route
  const getDateConstraints = (bedIndex: number) => {
    if (!room?.b?.[bedIndex]?.a?.length) {
      return null;
    }

    const assignments = room.b[bedIndex].a;
    
    // Convert dates to timestamps if they are string dates (DD/MM/YYYY)
    const checkInDates = assignments.map(a => {
      if (typeof a.ci === 'string' && a.ci.includes('/')) {
        const [day, month, year] = a.ci.split('/').map(Number);
        return new Date(year, month - 1, day).getTime();
      }
      return a.ci;
    });
    
    const checkOutDates = assignments.map(a => {
      if (typeof a.co === 'string' && a.co.includes('/')) {
        const [day, month, year] = a.co.split('/').map(Number);
        return new Date(year, month - 1, day).getTime();
      }
      return a.co;
    });
    
    const oldestCheckIn = Math.min(...checkInDates);
    const newestCheckOut = Math.max(...checkOutDates);

    return {
      oldestCheckIn,
      newestCheckOut
    };
  };

  // Define loadRoom as a component method
  const loadRoom = async () => {
    console.log("🔄 Loading room details:", { tableId, roomNumber });
    setIsLoading(true);
    setError(null);
    try {
      const details = await getRoomDetails(tableId, roomNumber);
      console.log("✅ Room details loaded:", details);
      setRoom(details);
    } catch (error: any) {
      console.error('❌ Error loading room details:', error);
      setError(
        translations?.errors?.loadRoom || 'Failed to load room details'
      );
      toast.error(
        translations?.errors?.loadRoom || 'Failed to load room details'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadRoom();
    }
  }, [tableId, roomNumber, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newRoomNumber || isNaN(parseInt(newRoomNumber))) {
      toast.error(
        translations?.errors?.invalidRoomNumber || 'Please enter a valid room number.'
      );
      return;
    }

    try {
      await updateRoomNumber(tableId, roomNumber, parseInt(newRoomNumber));
      toast.success(
        translations?.success?.updateRoomNumber || 'Room number successfully updated.'
      );
      setIsEditing(false);
      onClose();
    } catch (error: any) {
      toast.error(
        error.message || translations?.errors?.updateRoomNumber || 'Failed to update room number.'
      );
    }
  };

  const handleRemovePilgrim = async (bedIndex: number, pilgrimId: string) => {
    if (isRemoving) return;
    
    try {
      setIsRemoving(true);
      await removePilgrim(tableId, roomNumber, bedIndex, pilgrimId, translations);
      await loadRoom(); // Reload room data after removal
      toast.success(translations?.success?.removePilgrim || 'Pilgrim removed successfully');
      setIsDeleteConfirmOpen(false);
    } catch (error: any) {
      console.error('Error removing pilgrim:', error);
      toast.error(error.message || translations?.errors?.removePilgrim || 'Failed to remove pilgrim');
    } finally {
      setIsRemoving(false);
    }
  };

  const handleAddComment = async () => {
    if (!commentText.trim()) {
      toast.error(translations?.errors?.emptyComment || 'Comment text is required.');
      return;
    }

    if (!userId || !userName) {
      toast.error(translations?.errors?.notLoggedIn || 'You must be logged in to add a comment.');
      return;
    }

    try {
      await addComment(tableId, roomNumber, {
        userId,
        userName,
        text: commentText.trim(),
        createdAt: Date.now(),
      });
      setCommentText('');
      await loadRoom();
      toast.success(translations?.success?.addComment || 'Comment added successfully.');
    } catch (error: any) {
      console.error('Error adding comment:', error);
      toast.error(error.message || translations?.errors?.addComment || 'Failed to add comment.');
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteComment(tableId, roomNumber, commentId);
      await loadRoom();
      toast.success(
        translations?.success?.deleteComment || 'Comment successfully deleted.'
      );
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.error(
        translations?.errors?.deleteComment || 'Failed to delete comment.'
      );
    }
  };

  const handleAssignPilgrim = async (pilgrim: any) => {
    try {
      console.log('Starting pilgrim assignment with data:', {
        pilgrimData: pilgrim.pilgrimData,
        guideData: pilgrim.guideData,
        organizerData: pilgrim.organizerData,
        packageData: pilgrim.packageData
      });

      // Get hotel dates from pilgrim data
      let checkIn = '';
      let checkOut = '';
      
      if (table.hn === pilgrim.packageData.mainMakkah.hotel) {
        checkIn = pilgrim.packageData.mainMakkah.startDate;
        checkOut = pilgrim.packageData.mainMakkah.endDate;
      } else if (pilgrim.packageData.shiftingMakkah && table.hn === pilgrim.packageData.shiftingMakkah.hotel) {
        checkIn = pilgrim.packageData.shiftingMakkah.startDate;
        checkOut = pilgrim.packageData.shiftingMakkah.endDate;
      } else if (table.hn === pilgrim.packageData.madinah.hotel) {
        checkIn = pilgrim.packageData.madinah.startDate;
        checkOut = pilgrim.packageData.madinah.endDate;
      } else {
        throw new Error(translations?.roomViewModal?.errors?.hotelNotInPackage || 'This hotel is not assigned in the pilgrim\'s package');
      }

      console.log('Date range determined:', { checkIn, checkOut });

      await assignPilgrim(
        tableId,
        roomNumber,
        selectedBed!,
        pilgrim.pilgrimData.passportNumber,
        {
          checkIn,
          checkOut
        },
        translations
      );

      console.log('Assignment successful, reloading room data');
      await loadRoom();
      toast.success(translations?.roomViewModal?.success?.assignPilgrim || 'Pilgrim assigned successfully');
      setSelectedBed(null);
    } catch (error: any) {
      console.error('Error assigning pilgrim:', error);
      toast.error(error.message || translations?.roomViewModal?.errors?.assignPilgrim || 'Failed to assign pilgrim');
    }
  };

  const handleAssignEmployeeOrGuide = async (data: {
    name: string;
    gender: 'M' | 'F';
    isGuide: boolean;
    guideId?: string;
    guideName?: string;
    organizerId?: string;
    organizerName?: string;
  }) => {
    try {
      if (selectedBed === null) {
        toast.error('No bed selected');
        return;
      }

      console.log('Assigning employee or guide:', data);
      
      // Get current date in DD/MM/YYYY format
      const today = new Date();
      const dd = String(today.getDate()).padStart(2, '0');
      const mm = String(today.getMonth() + 1).padStart(2, '0');
      const yyyy = today.getFullYear();
      const currentDate = `${dd}/${mm}/${yyyy}`;
      
      // Set checkout date to one year from now
      const futureDate = new Date(today.getFullYear() + 1, today.getMonth(), today.getDate());
      const futureDd = String(futureDate.getDate()).padStart(2, '0');
      const futureMm = String(futureDate.getMonth() + 1).padStart(2, '0');
      const futureYyyy = futureDate.getFullYear();
      const futureFormattedDate = `${futureDd}/${futureMm}/${futureYyyy}`;
      
      // Create a simple assignment object with just the required fields
      // For employees, we'll just use the name and gender
      // For guides, we'll include their guide and organizer information
      const assignment = {
        pid: data.isGuide ? data.guideId! : `emp-${Date.now()}`, // Generate a unique ID for employees
        pn: data.name,
        ci: currentDate,
        co: futureFormattedDate,
        g: data.gender,
        gid: data.isGuide ? data.guideId! : userId || 'unknown',
        gn: data.isGuide ? data.guideName! : userName || 'Admin',
        oid: data.isGuide ? data.organizerId! : userId || 'unknown',
        on: data.isGuide ? data.organizerName! : userName || 'Admin',
        age: 30, // Default age for employees/guides
        isStaff: true, // Add a flag to identify staff members
      };

      // Get the table document ID and room information
      const tableRef = tables[tableId];
      if (!tableRef) {
        throw new Error('Table not found');
      }

      const roomIndex = tableRef.r.findIndex(r => r.n === roomNumber.toString());
      if (roomIndex === -1) {
        throw new Error('Room not found');
      }

      const updatedRooms = [...tableRef.r];
      const updatedBeds = [...updatedRooms[roomIndex].b];
      
      if (!updatedBeds[selectedBed].a) {
        updatedBeds[selectedBed].a = [];
      }
      
      updatedBeds[selectedBed].a.push(assignment);
      updatedRooms[roomIndex].b = updatedBeds;
      updatedRooms[roomIndex].o++;

      // Update the table with the new assignment
      await useAccommodationStore.getState().updateRooms(tableId, updatedRooms);
      
      // Reload the room data
      await loadRoom();
      
      toast.success(data.isGuide ? 
        'Guide assigned successfully' : 
        'Employee assigned successfully');
      
      setSelectedBed(null);
      setIsEmployeeGuideModalOpen(false);
    } catch (error: any) {
      console.error('Error assigning employee or guide:', error);
      toast.error(error.message || 'Failed to assign employee or guide');
    }
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`${translations?.roomViewModal?.title || 'Room'} ${roomNumber}`}
      width="90vw"
      maxWidth="800px"
    >
      {userRole === 'admin' && (
        <div className="absolute left-12 top-4">
          <button
            onClick={() => setIsEditing(true)}
            className="p-2 text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
            title={translations?.roomViewModal?.editTitle || 'Edit room number'}
          >
            <Edit size={18} />
          </button>
        </div>
      )}

      {isEditing && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <input
              type="number"
              value={newRoomNumber}
              onChange={(e) => setNewRoomNumber(e.target.value)}
              placeholder={translations?.roomViewModal?.placeholder || 'Enter new room number'}
              className="w-full md:w-auto flex-1 px-4 py-2 border rounded-lg text-right focus:ring-2 focus:ring-primary-500"
            />
            <div className="flex gap-2 w-full md:w-auto">
              <button
                onClick={handleSubmit}
                className="flex-1 md:flex-none px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
              >
                {translations?.actions?.update || 'Update'}
              </button>
              <button
                onClick={() => setIsEditing(false)}
                className="flex-1 md:flex-none px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                {translations?.actions?.cancel || 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center items-center p-8">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="p-4 text-center text-red-600">{error}</div>
      ) : room ? (
        <div className="space-y-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between">
            <span className="text-sm text-gray-500">
              {translations?.roomViewModal?.roomType || 'Room type'}:{' '}
              {room.t === 'd'
                ? translations?.roomViewModal?.double || 'Double'
                : room.t === 't'
                ? translations?.roomViewModal?.triple || 'Triple'
                : translations?.roomViewModal?.quadruple || 'Quadruple'}
            </span>
            <span className="text-sm text-gray-500">
              {translations?.roomViewModal?.bedsCount || 'Number of beds'}: {room.b.length}
            </span>
            
            {/* Accommodate Guide/Employee Button - Only for admin */}
            {userRole === 'admin' && (
              <button
                onClick={() => {
                  if (selectedBed === null) {
                    toast.error('Please select a bed first');
                    return;
                  }
                  setIsEmployeeGuideModalOpen(true);
                }}
                className="flex items-center gap-2 px-4 py-2 mt-2 md:mt-0 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <UserPlus size={18} />
                <span>
                  {translations?.roomViewModal?.accommodateStaff || 'Accommodate Guide/Employee'}
                </span>
              </button>
            )}
          </div>

          <BedList
            beds={room.b}
            selectedBed={selectedBed}
            onSelectBed={setSelectedBed}
            onRemove={async (bedIndex, pilgrimId) => handleRemovePilgrim(bedIndex, pilgrimId)}
            userRole={userRole || ''}
            userId={userId || ''} // Pass the actual userId
            hotelName={table.hn}
            hotelType={determineHotelType(table.hn)}
            allRoomAssignments={room.b.flatMap(bed => bed.a || [])} // Pass all room assignments
          />

          {selectedBed !== null && canAssignPilgrims && (
            <div className="border-t pt-4">
              <h3 className="font-medium mb-4">
                {translations?.roomViewModal?.assignNew || 'Assign new pilgrim'}
              </h3>
              <PilgrimSelector 
                onSelect={handleAssignPilgrim} 
                roomType={room.t}
                hotelName={table.hn}
                bedIndex={selectedBed}
                currentAssignments={room.b[selectedBed].a || []}
                dateConstraints={getDateConstraints(selectedBed)}
              />
            </div>
          )}
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={isDeleteConfirmOpen}
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => selectedBed !== null && handleRemovePilgrim(selectedBed, '')}
        title={translations?.confirmDialog?.deleteTitle || 'Delete Assignment'}
        message={translations?.confirmDialog?.deleteMessage || 'Are you sure you want to remove this pilgrim?'}
        type="danger"
        confirmText={translations?.actions?.delete || 'Delete'}
        cancelText={translations?.actions?.cancel || 'Cancel'}
      />

      {/* Employee/Guide Modal */}
      {isEmployeeGuideModalOpen && selectedBed !== null && (
        <EmployeeGuideModal
          isOpen={true}
          onClose={() => setIsEmployeeGuideModalOpen(false)}
          onAssign={handleAssignEmployeeOrGuide}
        />
      )}
    </Modal>
  );
};

// Helper function to determine hotel type
const determineHotelType = (hotelName: string): 'mainMakkah' | 'shiftingMakkah' | 'madinah' => {
  const packages = usePackagesStore.getState().packages;
  
  // Iterate through packages object
  for (const packageId in packages) {
    const pkg = packages[packageId];
    
    if (pkg.mainMakkahHotel === hotelName) return 'mainMakkah';
    if (pkg.shiftingMakkahHotel === hotelName) return 'shiftingMakkah';
    if (pkg.madinahHotel === hotelName) return 'madinah';
  }

  return 'mainMakkah'; // Default fallback
};